import { CheckCircle } from "@phosphor-icons/react";
import { PageHero, CTASection } from "../components/Sections";
import { Reveal, Overline } from "../components/Reveal";
import { useSettings } from "../hooks/usePublicContent";
import { IMAGES, VALUES, LOGO_AIBM } from "../data/content";

const extractYT = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/|live\/))([\w-]{11})/);
  return m ? m[1] : null;
};

const journey = [
  { year: "I primi anni", text: "L'inizio dietro il bancone: gavetta, turni, la disciplina del servizio reale imparata sul campo." },
  { year: "La crescita", text: "Da bartender a responsabile: gestione della sala, della squadra e della carta. Capire i numeri, non solo i drink." },
  { year: "La formazione", text: "L'ingresso nel mondo della docenza e l'adesione all'AIBM: trasferire metodo e standard a chi vuole crescere." },
  { year: "Oggi", text: "Consulente, formatore e Open Bar Specialist. Un unico riferimento per locali, aziende e privati." },
];

export default function ChiSono() {
  const s = useSettings();
  const ytId = extractYT(s.interview_url);
  return (
    <div data-testid="chi-sono-page">
      <PageHero
        overline="Luigi Giuli · Bar Consultant & Trainer"
        title="20 anni di mestiere, un solo modo di lavorare: sul serio."
        subtitle="Docente, socio AIBM, consulente e Open Bar Specialist. Non vendo estetica: risolvo problemi e faccio crescere i tuoi numeri."
        image={IMAGES.shelves}
      />

      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <img src={IMAGES.about} alt="Ritratto professionale" className="w-full h-[560px] object-cover rounded-sm" />
          </Reveal>
          <Reveal delay={0.15}>
            <Overline>La mia filosofia</Overline>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold">Il bar è servizio, cultura e relazione</h2>
            <p className="mt-6 text-[#A1A1AA] text-lg leading-relaxed">
              Ho iniziato dalla gavetta e ho attraversato ogni ruolo: dal bancone alla gestione, dalla sala alla formazione. Questo mi permette di vedere un locale nel suo insieme — non solo i cocktail, ma l'organizzazione, i tempi, i margini e l'esperienza del cliente.
            </p>
            <p className="mt-4 text-[#A1A1AA] text-lg leading-relaxed">
              Come docente e socio AIBM, porto metodo e standard riconosciuti. Come consulente, lavoro per obiettivi concreti. Come Open Bar Specialist, costruisco Open Bar tematici che restano impressi.
            </p>
            <div className="mt-8 space-y-3">
              {["Esperienza reale, non solo teoria", "Metodo e standard AIBM", "Approccio orientato ai risultati", "Formazione su misura per ogni target"].map((p) => (
                <div key={p} className="flex items-center gap-3">
                  <CheckCircle size={22} weight="fill" className="text-[#D97736] shrink-0" />
                  <span className="text-[#F8F8F8]">{p}</span>
                </div>
              ))}
            </div>
            <div className="mt-8 flex items-center gap-4 bg-[#111113] border border-white/5 rounded-sm p-5 w-fit">
              <img src={LOGO_AIBM} alt="AIBM Project" className="h-16 w-16 object-contain bg-white rounded-sm p-1.5" />
              <div>
                <div className="text-xs uppercase tracking-wider text-[#D97736] font-semibold">Socio & Docente</div>
                <div className="font-semibold text-[#F8F8F8]">AIBM</div>
                <div className="text-sm text-[#A1A1AA]">Associazione Italiana Bartender & Mixologist</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {ytId && (
        <section className="pb-8">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <Reveal className="max-w-2xl">
              <Overline>L'intervista</Overline>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold">Guarda la mia storia</h2>
              <p className="mt-4 text-[#A1A1AA] text-lg">Un racconto in prima persona del mio percorso, della mia visione e del modo in cui lavoro.</p>
            </Reveal>
            <Reveal delay={0.1}>
              <div data-testid="interview-video" className="mt-10 relative w-full rounded-sm overflow-hidden border border-white/10" style={{ aspectRatio: "16 / 9" }}>
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src={`https://www.youtube.com/embed/${ytId}`}
                  title="Intervista a Luigi Giuli"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </Reveal>
          </div>
        </section>
      )}

      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="max-w-2xl">
            <Overline>Il percorso</Overline>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold">Dalla gavetta alla consulenza</h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            {journey.map((j, i) => (
              <Reveal key={j.year} delay={i * 0.1}>
                <div className="bg-[#111113] border border-white/5 p-8 rounded-sm h-full">
                  <span className="text-[#D97736] font-display text-xl font-semibold">{j.year}</span>
                  <p className="mt-3 text-[#A1A1AA] leading-relaxed">{j.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-24 sm:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.08}>
              <div className="bg-[#111113] border border-white/5 p-6 rounded-sm h-full">
                <h3 className="font-semibold text-[#F8F8F8]">{v.title}</h3>
                <p className="text-sm text-[#A1A1AA] mt-2">{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <CTASection />
    </div>
  );
}
